import type { FromBaseRow as Row } from './from-base-context';
import { fromBaseRecord } from './from-base-context';
import type { FromBasePlayerIntegerMetadata } from './from-base-player-integers';
import { createFromBasePlayerGrantContext } from './from-base-player-grants';
import { createNestedPassiveCollector } from './nested-passive-fold';
import { createDynamicIntegerSourceCollector } from './dynamic-integer-sources';
import { createCreatureEssenceRollupWithSourceCleanup } from './creature-essence-rollup';
import { createCreatureEquippedCountsCompiler } from './creature-equipped-counts';
import { createSkillRequirementChecker } from './skill-requirements';
import { readAbilityEntryId, containsAbilityTriggerKey } from './inventory-ability-grant-helpers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { readItemField, type ItemFieldRow } from './item-field-reader';
import { integerCompileWorkingRow, integerCompileResult, putIntegerCompilePath, removeIntegerCompilePath,
    writeIntegerComputeSlotValue } from './integer-compute-slots';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { sameTree } from './tree-transfer';
import { foldCreatureDefenseEntries } from './creature-defense';
import { compileCreatureAttacks } from './creature-attacks';
import { DEFENSE_SLOTS } from './creature-defense-delta';
import { constructPlayerSkillSourcesFromBase } from './from-base-player-skill-sources';
import { constructNpcSkillSourcesFromBase } from './from-base-npc-skill-sources';
import { compileCreatureSkillTier } from './creature-skill-tier';
import { storeCreatureSkillEntry } from './creature-skill-entry-storage';
import { createActorAbilityMaterializer, playerSpellKnownPointers } from './from-base-player-abilities';
import { finalizeNonChargeAbilityBucket } from './non-charge-ability-bucket';
import { groupInnateSpells } from './innate-spell-grouping';
import { compileVitality } from './vitality-build';
import { createStatusStackReader } from './status-stack-reader';
import { readCreatureStatuses } from './creature-status-reader';
import type { FromBasePlayerInput } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import type { CreatureDefenseFoldInput } from '../types';
import { constructNpcCastableFromBase } from './from-base-npc-castable';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { playerChoiceGrant } from './player-choice-grant';

type Context = Pick<ReturnType<typeof createFromBasePlayerGrantContext>, 'readers' | 'definition' | 'deps'>;
type Eligible = (ability: ItemFieldRow, entry: unknown, host: Row) => boolean;
const record = (value: unknown): Row => fromBaseRecord(value) ? value : {};
const upper = (value: unknown) => phpJsonString(value ?? '').toUpperCase();
const creature = (host: Row) => record(record(host.computed).creature);
const entries = (context: Context, row: ItemFieldRow, key: string) => Object.values(context.readers.layerEntries(row, key));
const idOf = (context: Context, row: unknown) => upper(readAbilityEntryId(row,
    { itemField: context.readers.itemField, resolveCarrierRef: context.definition }));
const definitionOf = (context: Context, row: unknown) => context.definition(idOf(context, row));

/** Charge ceilings use only the active held sources, just as native birth does. */
export function savedGrantAbilityMaterializer(host: Row, context: Context, base: FromBasePlayerInput,
    metadata: FromBasePlayerIntegerMetadata, eligible: Eligible) {
    const active = ['feat', 'passive_ability'].flatMap(key => Object.values(record(creature(host).abilities)[key] ?? {}).filter(entry => {
        const def = definitionOf(context, entry);
        return def && eligible(def, entry, host) && !containsAbilityTriggerKey(def);
    }));
    return createActorAbilityMaterializer(base, metadata as FromBasePlayerResourceMetadata,
        { ...host, feat: active, passive_ability: [] }, context);
}

/** Source-local execution/authoring layers stay on item_arr. Only cluster
 * producers need a held projection. Keep the genuinely unported producers
 * explicit, so adding roll/target/offer metadata cannot blank a creature. */
export function savedGrantLayerFamily(key: string, metadata: FromBasePlayerIntegerMetadata): string {
    if (DEFENSE_SLOTS[key]) return 'defenses';
    if (/^skill_tier_\d+$/.test(key) || ['skill_roll_adv', 'skill_roll_dis', 'skill_roll_mod', 'skill_points'].includes(key)) return 'skills';
    if (['increase_max_charges_of_ability', 'increase_max_charges_by_qty',
        'decrease_max_charges_of_ability', 'decrease_max_charges_by_qty'].includes(key)) return 'charges';
    if (['at_will_spell', 'innate_spell_per_long_rest', 'spell_known', 'curse_known'].includes(key)) return 'spells';
    if (key === 'increase_max_v_per_level') return 'vitality';
    if (key === 'feat_essence' || metadata.preliminary.allEssences.some(row =>
        [row.variable_name, `feat_${row.variable_name}`, `ability_${row.variable_name}`].includes(key))) return 'essences';
    if (metadata.rollup.integerLayers.includes(key)) return 'integers';
    if (['size', 'creature_type', 'creature_sub_type', 'status_effect_permanent', 'override_ability'].includes(key)) return 'unported';
    // Passive natural_armor/robust_build are not host layers: the native
    // recount/capacity windows read host/provider layers, not passive item_arr.
    return 'source';
}

type DefenseEvent = CreatureDefenseFoldInput['events'][number];
/** Fold only UUIDs whose source events changed. Other defense rows retain
 * identity; removing a primary promotes its first surviving contributor. */
export async function savedGrantDefenseDelta(host: Row, layer: string, oldEvents: readonly DefenseEvent[],
    nextEvents: readonly DefenseEvent[]): Promise<Row> {
    const spec = DEFENSE_SLOTS[layer];
    if (!spec) return host;
    const ids = new Set([...oldEvents, ...nextEvents].map(row => row.uuid));
    const defenses = record(creature(host).defenses), stored = Object.values(defenses[spec.slot] ?? {});
    const sourceKey = (source: Row) => [readItemField(source, 'source_item_id'), readItemField(source, 'source_kind')].map(upper).join('|');
    const next = [...stored];
    for (const id of ids) {
        const old = oldEvents.filter(row => row.uuid === id), added = nextEvents.filter(row => row.uuid === id);
        if (await sameTree(old, added)) continue;
        const at = next.findIndex(row => upper(readItemField(record(row), spec.key) ?? readLayerListEntryUuid(row)) === upper(id));
        const prior = at < 0 ? null : next[at], row = record(prior);
        const removed = new Set(old.map(event => sourceKey(event.source)));
        const sources = [row, ...Object.values(row.all_sources ?? {}).filter(fromBaseRecord)]
            .filter(source => !removed.has(sourceKey(source)));
        const initial = sources.length && prior != null ? [{ [spec.key]: id, ...sources[0],
            all_sources: sources.slice(1) }] : [];
        // The primary's old contributor list was flattened above.
        if (initial.length) initial[0]!.all_sources = sources.slice(1).map(source => {
            const { all_sources: _nested, [spec.key]: _identity, ...meta } = source; return meta;
        });
        const folded = foldCreatureDefenseEntries({ family: spec.key === 'damage_type' ? 'damage' : 'status',
            initialEntries: initial, events: added });
        if (at >= 0) next.splice(at, 1, ...folded); else next.push(...folded);
    }
    if (next.length === stored.length && next.every((row, i) => row === stored[i])) return host;
    const work = integerCompileWorkingRow(host);
    if (next.length) putIntegerCompilePath(work, ['computed', 'creature', 'defenses', spec.slot], next);
    else removeIntegerCompilePath(work, ['computed', 'creature', 'defenses', spec.slot]);
    return integerCompileResult(work) as Row;
}

/** The normal castable compiler bakes only the changed spell rows. Its temporary
 * output is merged into the held bucket; it never sees an entire spell book. */
export async function savedGrantCastableDelta(previous: Row, host: Row, context: Context, base: FromBasePlayerInput,
    metadata: FromBasePlayerIntegerMetadata): Promise<Row> {
    const keys = ['at_will_spell', 'innate_spell_per_long_rest', 'spells_known', 'curses_known'];
    const affected = new Map<string, Set<string>>();
    for (const key of keys) {
        const old = entries(context, previous, key), next = entries(context, host, key);
        const ids = new Set([...old, ...next].map(row => idOf(context, row)));
        for (const id of ids) if (!await sameTree(old.filter(row => idOf(context, row) === id), next.filter(row => idOf(context, row) === id)))
            (affected.get(key) ?? (affected.set(key, new Set()), affected.get(key)!)).add(id);
    }
    if (!affected.size) return host;
    const projection = { ...host, computed: { ...record(host.computed), creature: {
        ...creature(host), spellcasting: { ...record(creature(host).spellcasting) },
    } } };
    const windowEntries: typeof context.readers.layerEntries = (row, key) => row === projection && keys.includes(key)
        ? entries(context, host, key).filter(entry => affected.get(key)?.has(idOf(context, entry)))
        : row === projection && /^spell_prepared_/.test(key) ? [] : context.readers.layerEntries(row, key);
    constructNpcCastableFromBase(base as unknown as Row, metadata as FromBasePlayerResourceMetadata, projection, {
        readers: { ...context.readers, layerEntries: windowEntries },
        definition: id => { const def = context.definition(id); return fromBaseRecord(def) ? def : null; },
        common: { resolveCarrierRef: id => { const def = context.definition(id); return fromBaseRecord(def) ? def : null; } },
    }, [...affected.keys()]);
    const work = integerCompileWorkingRow(host), castable = record(creature(host).castable);
    for (const [key, ids] of affected) {
        const retained = Object.values(castable[key] ?? {}).filter(row => !ids.has(idOf(context, row)));
        const next = [...retained, ...Object.values(record(creature(projection).castable)[key] ?? {})];
        if (next.length) putIntegerCompilePath(work, ['computed', 'creature', 'castable', key], next);
        else removeIntegerCompilePath(work, ['computed', 'creature', 'castable', key]);
        if (key === 'spells_known') putIntegerCompilePath(work, ['computed', 'creature', 'spellcasting', 'max_spell_level_castable'],
            Math.max(-1, ...next.map(row => phpJsonInt(context.readers.layerValue(definitionOf(context, row), 'spell_level')))));
    }
    return integerCompileResult(work) as Row;
}

export interface SavedSpellGrant { id: string; source: string }
/** Add/retract native spell membership, including an innate group's independent
 * donors. Work is bounded to spell UUIDs carried by the changed sources. */
export function savedGrantSpellBucketDelta(host: Row, key: string, prior: readonly SavedSpellGrant[], next: readonly SavedSpellGrant[],
    context: Context, base: FromBasePlayerInput, metadata: FromBasePlayerIntegerMetadata): Row {
    const known = key.endsWith('_known'), bucket = known ? (key === 'spell_known' ? 'spells_known' : 'curses_known') : key;
    const original = Object.values((known ? creature(host) : record(creature(host).abilities))[bucket] ?? {});
    const affected = new Set([...prior, ...next].map(row => row.id));
    const removed = (id: string) => new Set(prior.filter(row => row.id === id).map(row => row.source));
    const independentKnown = (id: string) => {
        const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId,
            resolveCarrierRef: context.definition });
        return [...providers.providers(host)].some(provider => entries(context, provider, key)
            .some(row => upper(readLayerListEntryUuid(row)) === id));
    };
    const retained: unknown[] = [], touched: unknown[] = [];
    for (const entry of original) {
        const id = idOf(context, entry);
        if (!affected.has(id)) { retained.push(entry); continue; }
        const row = record(entry), source = upper(context.readers.itemField(row, 'source'));
        if (known) {
            if (!removed(id).has(source) && (source || independentKnown(id))) touched.push(entry);
        } else if (key === 'innate_spell_per_long_rest' && Object.keys(record(row.stacked_source_maxes)).length) {
            const kept = Object.fromEntries(Object.entries(record(row.stacked_source_maxes)).filter(([source]) => !removed(id).has(upper(source))));
            if (Object.keys(kept).length) touched.push({ ...row, stacked_source_maxes: kept });
        } else if (!removed(id).has(source)) touched.push(entry);
    }
    const choices = base.user_choices.filter(choice => affected.has(upper(context.readers.itemField(choice, 'choice'))))
        .map(choice => playerChoiceGrant(choice, base, metadata, context.definition, context.readers.layerValue))
        .filter(choice => choice.layer === key && choice.requiresLevel <= phpJsonInt(context.readers.layerValue(host, 'total_level')))
        .map(choice => ({ id: idOf(context, choice.entry), source: upper(context.readers.itemField(choice.entry, 'source')) }));
    for (const grant of [...next, ...choices]) {
        const def = context.definition(grant.id);
        if (!def) throw Error(`saved-grant-delta:missing-spell:${grant.id}`);
        touched.push({ item_arr: def, source: grant.source, requires_level: 1 });
    }
    const work = integerCompileWorkingRow(host);
    if (known) {
        const seen = new Set<string>();
        const pointers = playerSpellKnownPointers(touched.filter(row => {
            const id = idOf(context, row); if (seen.has(id)) return false; seen.add(id); return true;
        }).map(row => context.definition(idOf(context, row)) as Row), base.player_characters ?? {}, context.readers, context.definition);
        putIntegerCompilePath(work, ['computed', 'creature', bucket], [...retained, ...pointers]);
        const book = Object.values(record(record(host.computed)._cache).spell_book ?? {}).filter(row =>
            !affected.has(upper(context.readers.itemField(record(row), 'spell_id'))));
        for (const pointer of pointers) book.push({ spell_id: context.readers.itemField(pointer, 'item_id'), class_id: '', subclass_id: '' });
        if (key === 'spell_known') putIntegerCompilePath(work, ['computed', '_cache', 'spell_book'], book);
    } else {
        const projected = key === 'innate_spell_per_long_rest'
            ? groupInnateSpells({ itemId: base.entity_id, entries: touched.map(entry => {
                const row = record(entry), def = definitionOf(context, row);
                return { spellId: idOf(context, row), spellName: phpJsonString(context.readers.itemField(def, 'item_name')),
                    source: phpJsonString(context.readers.itemField(row, 'source')), stackedSourceMaxes: record(row.stacked_source_maxes) as Record<string, number>,
                    maxCharges: phpJsonInt(context.readers.itemField(record(row.charges), 'max') ?? 1) };
            }) }).map(group => {
                const saved = base.charges?.find(row => upper(context.readers.itemField(row, 'item_id')) === upper(group.spellId)
                    && upper(context.readers.itemField(row, 'charge_holder')) === upper(context.readers.itemField(record(host.encounter_creatures), 'encounter_creature_id') ?? base.entity_id)
                    && !phpJsonInt(context.readers.itemField(row, 'charge_deleted')));
                return { item_arr: context.definition(group.spellId), source: group.firstSource, requires_level: 1,
                    stacked_source_maxes: group.sourceMaxes, charges: { current: saved ? phpJsonInt(context.readers.itemField(saved, 'charges')) : group.maxUses,
                        max: group.maxUses, resets_long_rest: 1 } };
            })
            : finalizeNonChargeAbilityBucket({ layer: key, entries: touched.map((entry, index) => ({
                abilityId: idOf(context, entry), source: phpJsonString(context.readers.itemField(record(entry), 'source')), requiresLevel: 1,
                name: phpJsonString(context.readers.itemField(definitionOf(context, entry), 'item_name')), drawsSheathedWeapon: false, token: String(index),
            })) }).map(plan => touched[Number(plan.token)]);
        putIntegerCompilePath(work, ['computed', 'creature', 'abilities', key], [...retained, ...projected]);
    }
    return integerCompileResult(work) as Row;
}

/** Reconcile only membership granted by held feat/passive sources. The initial
 * passive window and this saved-row window share the same one-level collector. */
export function savedNestedGrantAbilities(previous: Row, host: Row, context: Context, eligible: Eligible,
    affected: ReadonlySet<string>): Row {
    const oldAbilities = record(creature(previous).abilities), abilities = record(creature(host).abilities);
    const parentIds = new Set(affected), sharedChildren = new Set<string>();
    for (const id of affected) {
        const def = context.definition(id);
        if (def) for (const row of entries(context, def, 'passive_ability')) sharedChildren.add(upper(readLayerListEntryUuid(row)));
    }
    for (const bucket of [oldAbilities, abilities]) for (const row of Object.values(bucket.passive_ability ?? {})) {
        const def = definitionOf(context, row);
        if (def && entries(context, def, 'passive_ability').some(row => sharedChildren.has(upper(readLayerListEntryUuid(row)))))
            parentIds.add(idOf(context, row));
    }
    const result: Row = { ...abilities };
    for (const key of new Set([...Object.keys(abilities), 'passive_ability'])) {
        const list = Object.values(abilities[key] ?? {});
        const retained = list.filter(row => !parentIds.has(upper(readItemField(record(row), 'source'))));
        if (retained.length !== list.length) result[key] = retained;
    }
    const working = { ...host, computed: { ...record(host.computed), creature: { ...creature(host), abilities: result } } };
    const append = (key: string, row: Row) => {
        const id = idOf(context, row), list = Object.values(result[key] ?? {});
        if (!list.some(entry => idOf(context, entry) === id)) result[key] = [...list, row];
    };
    for (const entry of Object.values(result.feat ?? {})) {
        if (!parentIds.has(idOf(context, entry))) continue;
        const feat = definitionOf(context, entry);
        if (!feat || !eligible(feat, entry, working)) continue;
        for (const key of new Set([...context.deps.abilityTypes, 'passive_ability'])) for (const raw of entries(context, feat, key)) {
            const id = readLayerListEntryUuid(raw), ability = context.definition(id);
            if (ability && eligible(ability, entry, working)) append(key, { ability_id: id,
                source: idOf(context, entry), item_arr: ability, requires_level: 1, ft_granted_by_feat: true });
        }
    }
    const collect = createNestedPassiveCollector({ ...context.deps,
        isAura: row => ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target']
            .every(key => context.readers.layerValue(row, key) !== null) });
    // Ineligible parents must not donate nested membership. Retain them in the
    // real bucket, so a later condition/level change can reactivate them.
    const active = Object.values(result.passive_ability ?? {}).filter(entry => {
        const ability = definitionOf(context, entry);
        if (!ability || !eligible(ability, entry, working)) return false;
        if (parentIds.has(idOf(context, entry)) && entries(context, ability, 'passive_ability').length
            && Object.keys(record(record(ability).item_layer_types)).some(key => key.startsWith('if_')))
            throw Error(`saved-grant-delta:unported-conditioned-nested:${idOf(context, entry)}`);
        return true;
    });
    const probe = { ...working, computed: { ...record(working.computed),
        creature: { ...creature(working), abilities: { ...result, passive_ability: active } } } };
    for (const row of collect(probe)) if (parentIds.has(upper(readItemField(row, 'source')))) append('passive_ability', row);
    for (const key of Object.keys(result)) {
        const prior = Object.values(oldAbilities[key] ?? {});
        result[key] = Object.values(result[key] ?? {}).map(row => prior.find(old => idOf(context, old) === idOf(context, row)
            && readItemField(record(old), 'source') === readItemField(record(row), 'source')
            && record(old).item_arr === record(row).item_arr) ?? row);
    }
    return result;
}

/** Find changed rule families from real definition reads, including unchanged
 * UUIDs. Unsupported effects name their exact dependency; null never means
 * a successful saved grant should be reloaded/recompiled. */
export async function savedGrantDependencyDelta(previous: Row, input: Row, before: Context, after: Context,
    metadata: FromBasePlayerIntegerMetadata, eligible: Eligible, beforeBase: FromBasePlayerInput,
    afterBase: FromBasePlayerInput): Promise<{ host: Row; families: string[] }> {
    const changed = new Set<string>();
    const sources = (host: Row, context: Context) => ['feat', 'passive_ability'].flatMap(key =>
        entries(context, host, key).flatMap(entry => {
            const def = definitionOf(context, entry);
            return def && eligible(def, entry, host) && !containsAbilityTriggerKey(def) ? [def] : [];
        }));
    const priorSources = sources(previous, before), nextSources = sources(input, after);
    const keys = new Set([...priorSources, ...nextSources].flatMap(def => Object.keys(record(record(def).item_layer_types))));
    for (const key of keys) {
        const values = (sources: ItemFieldRow[], context: Context) => sources.map(def =>
            [idOf(context, def), entries(context, def, key)] as const).filter(([, rows]) => rows.length)
            .sort(([a], [b]) => a.localeCompare(b));
        const old = values(priorSources, before), next = values(nextSources, after);
        if (!await sameTree(old, next)) changed.add(key);
    }
    const essences = metadata.preliminary.allEssences.filter(essence =>
        [essence.variable_name, `feat_${essence.variable_name}`, `ability_${essence.variable_name}`,
            'feat_essence'].some(key => changed.has(key)));
    const essenceKeys = new Set(metadata.preliminary.allEssences.flatMap(essence =>
        [essence.variable_name, `feat_${essence.variable_name}`, `ability_${essence.variable_name}`]));
    const integers = [...changed].filter(key => metadata.rollup.integerLayers.includes(key) && !essenceKeys.has(key) && key !== 'extra_attack');
    for (const key of changed) if (savedGrantLayerFamily(key, metadata) === 'unported')
        throw Error(`saved-grant-delta:unported-dependency:${key}`);
    const families = new Set([...changed].map(key => savedGrantLayerFamily(key, metadata)).filter(family => family !== 'source'));
    let host = input;
    const defenseEvents = (holder: Row, context: Context, key: string): DefenseEvent[] =>
        ['feat', 'passive_ability'].flatMap(kind => entries(context, holder, kind).flatMap(entry => {
            const def = definitionOf(context, entry);
            if (!def || !eligible(def, entry, holder) || containsAbilityTriggerKey(def)) return [];
            return entries(context, def, key).map(row => ({ uuid: phpJsonString(readItemField(record(row), DEFENSE_SLOTS[key]!.key)
                ?? readLayerListEntryUuid(row)), source: { source_item_id: idOf(context, def),
                source_name: context.readers.itemField(def, 'item_name'), source_kind: kind } }));
        }));
    for (const key of changed) if (DEFENSE_SLOTS[key])
        host = await savedGrantDefenseDelta(host, key, defenseEvents(previous, before, key), defenseEvents(host, after, key));
    if (['skill_roll_adv', 'skill_roll_dis', 'skill_roll_mod'].some(key => changed.has(key))) {
        const sources = fromBaseRecord(afterBase.player_characters) ? constructPlayerSkillSourcesFromBase
            : (base: FromBasePlayerInput, metadata: FromBasePlayerIntegerMetadata, host: Row) =>
                constructNpcSkillSourcesFromBase(base as unknown as Row, metadata as FromBasePlayerResourceMetadata, host);
        const old = sources(beforeBase, metadata, previous);
        const next = sources(afterBase, metadata, host);
        const lines = (sources: typeof old, skill: typeof metadata.skills[number], context: Context): Row[] => {
            const matches = (raw: unknown) => upper(readLayerListEntryUuid(raw)) === upper(skill.item_id)
                || upper(typeof raw === 'string' ? raw : context.readers.layerValue(record(raw), 'variable_name')) === upper(skill.variable_name);
            const out: Row[] = [];
            for (const source of [...sources.static.containers, ...sources.carrier, ...sources.static.inventory, ...sources.static.statuses])
                for (const [list, sign] of [[source.adv_list, 1], [source.dis_list, -1]] as const)
                    for (const raw of list) if (matches(raw)) out.push({ value: sign, label: source.name, kind: source.kind, href_id: source.href });
            return out;
        };
        const sum = (rows: readonly Row[]) => rows.reduce((total, row) => total + phpJsonInt(readItemField(row, 'value')), 0);
        const replace = (stored: Row[], removed: Row[], added: Row[]) => {
            const consumed = new Set<number>();
            removed = removed.filter(row => {
                const at = added.findIndex((next, index) => !consumed.has(index) && ['value', 'label', 'kind', 'href_id']
                    .every(key => readItemField(row, key) === readItemField(next, key)));
                if (at < 0) return true;
                consumed.add(at); return false;
            });
            added = added.filter((_, index) => !consumed.has(index));
            const keys = removed.map(row => [upper(readItemField(row, 'href_id')), readItemField(row, 'value')].join('|'));
            const kept = stored.filter(row => {
                const at = keys.indexOf([upper(readItemField(row, 'href_id')), readItemField(row, 'value')].join('|'));
                if (at < 0) return true;
                keys.splice(at, 1); return false;
            });
            return [...kept, ...added.map(line => {
                const out = { ...line }, def = after.definition(phpJsonString(readItemField(line, 'href_id')));
                if (def && after.readers.itemField(def, 'item_name') === out.label) delete out.label;
                if (def && after.readers.itemField(def, 'item_type') === out.kind) delete out.kind;
                return out;
            })];
        };
        const work = integerCompileWorkingRow(host);
        for (const skill of metadata.skills) {
            const oldAdv = lines(old, skill, before), newAdv = lines(next, skill, after);
            const mods = (sources: typeof old) => sources.mods.filter(row => upper(row.skill) === upper(skill.item_id))
                .map(row => ({ value: row.mod, label: row.label, kind: row.kind, href_id: row.href }));
            const oldMod = mods(old), newMod = mods(next);
            const budgetChanged = changed.has('skill_points') && ['a', 't', 'as'].some(kind =>
                phpJsonInt(after.readers.itemField(afterBase.player_characters, `sp_${kind}_${skill.variable_name}`)) !== 0);
            if (await sameTree(oldAdv, newAdv) && await sameTree(oldMod, newMod) && !budgetChanged) continue;
            const held = record(record(creature(host).skills)[skill.variable_name]);
            // A budget grant does not allocate SP. The tier leaf consumes only
            // the player's persisted allocation, just as the initial compiler.
            const tier = compileCreatureSkillTier({ skill, hardcodedLevel: 0, tierCatalog: metadata.skillTiers,
                skillPoints: afterBase.player_characters ? {
                    allocated: phpJsonInt(after.readers.itemField(afterBase.player_characters, `sp_a_${skill.variable_name}`)),
                    training: phpJsonInt(after.readers.itemField(afterBase.player_characters, `sp_t_${skill.variable_name}`)),
                    ascension: phpJsonInt(after.readers.itemField(afterBase.player_characters, `sp_as_${skill.variable_name}`)),
                } : null });
            const adv = replace(Object.values(record(held.adv_dis_breakdown).lines ?? {}).filter(fromBaseRecord), oldAdv, newAdv);
            const mod = replace(Object.values(record(held.skill_roll_mod_breakdown).lines ?? {}).filter(fromBaseRecord), oldMod, newMod);
            const stored = storeCreatureSkillEntry({ assignedLevel: phpJsonInt(after.readers.itemField(held, 'tier_level')),
                sources: Object.values(held.sources ?? {}).map(phpJsonString).filter(source => source !== 'default_flawed'),
                rawSp: tier.raw_sp ? { ...tier.raw_sp } : null, itemType: skill.item_type,
                advDis: phpJsonInt(after.readers.itemField(held, 'adv_dis_stacks')) - sum(oldAdv) + sum(newAdv),
                reversibleBreakdown: Boolean(held.adv_dis_breakdown) && !phpJsonInt(after.readers.itemField(held, 'adv_dis_stacks')),
                advDisLines: adv, globalMod: phpJsonInt(after.readers.itemField(held, 'skill_rolls_mod')),
                perSkillMod: phpJsonInt(after.readers.itemField(held, 'skill_roll_mod')) - sum(oldMod) + sum(newMod), perSkillModLines: mod,
                forcedNaturalMainRoll: after.readers.itemField(held, 'forced_natural_main_roll') == null ? null
                    : phpJsonInt(after.readers.itemField(held, 'forced_natural_main_roll')) });
            if (await sameTree(held, stored ?? {})) continue;
            if (stored) putIntegerCompilePath(work, ['computed', 'creature', 'skills', skill.variable_name], {
                ...stored, ...(held.tier_grant_sources ? { tier_grant_sources: held.tier_grant_sources } : {}),
            });
            else removeIntegerCompilePath(work, ['computed', 'creature', 'skills', skill.variable_name]);
        }
        host = integerCompileResult(work) as Row;
    }
    const savedMaximum = after.readers.itemField(afterBase.encounter_creatures ?? null, 'v_max');
    const priorMaximum = before.readers.itemField(beforeBase.encounter_creatures ?? null, 'v_max');
    const maximumChanged = String(savedMaximum ?? '') !== String(priorMaximum ?? '');
    if (families.has('vitality') || maximumChanged) {
        const total = (list: ItemFieldRow[], context: Context) => list.reduce((sum, def) =>
            sum + phpJsonInt(context.readers.layerValue(def, 'increase_max_v_per_level')), 0);
        const delta = (total(nextSources, after) - total(priorSources, before))
            * phpJsonInt(after.readers.layerValue(host, 'total_level'));
        if (delta || maximumChanged) {
            families.add('vitality');
            const resource = metadata as FromBasePlayerResourceMetadata, vitality = record(record(host.computed).vitality);
            const maximum = maximumChanged ? phpJsonInt(savedMaximum)
                : phpJsonInt(after.readers.itemField(vitality, 'base_v_max')) + delta;
            const stacks = createStatusStackReader(resource.vitality.statusCatalog, {
                readStatuses: row => readCreatureStatuses(row, 'target', after.definition), readItemField: after.readers.itemField,
            });
            // compileVitality owns the entire vitality cell, so give it that
            // cell's inputs, never the whole creature to clone.
            const projected = compileVitality({ items: { item_type: after.readers.itemField(host, 'item_type') } }, {
                v_max: Math.max(1, maximum),
                v_current: phpJsonInt(after.readers.itemField(vitality, 'base_v_current')),
                v_temp: phpJsonInt(after.readers.itemField(vitality, 'base_v_temp')),
                v_max_temp: phpJsonInt(after.readers.itemField(vitality, 'base_v_max_temp')),
            }, { readVitality: () => vitality, readLayerValue: after.readers.layerValue, readItemField: after.readers.itemField,
                readStatuses: () => readCreatureStatuses(host, 'target', after.definition),
                readBlightedStack: () => { const id = stacks.id('blighted'); return id ? stacks.read(host, id) : 0; },
                buildStatusShell: id => {
                    const row = resource.vitality.vitalityCatalog.find(row => upper(readItemField(row, 'item_id')) === upper(id));
                    return row ? { items: Object.fromEntries(['item_id', 'item_name', 'item_type'].map(key => [key, readItemField(row, key)])) } : null;
                } });
            const work = integerCompileWorkingRow(host);
            putIntegerCompilePath(work, ['computed', 'vitality'], record(record(projected.row).computed).vitality);
            host = integerCompileResult(work) as Row;
        }
    }
    if (families.has('charges')) {
        const targetIds = new Set([...priorSources.flatMap(def => ['increase_max_charges_of_ability', 'decrease_max_charges_of_ability']
            .flatMap(key => entries(before, def, key))), ...nextSources.flatMap(def => ['increase_max_charges_of_ability', 'decrease_max_charges_of_ability']
            .flatMap(key => entries(after, def, key)))].map(readLayerListEntryUuid).map(upper));
        const materializer = savedGrantAbilityMaterializer(host, after, afterBase, metadata, eligible);
        const work = integerCompileWorkingRow(host);
        for (const [key, bucket] of Object.entries(record(creature(host).abilities))) {
            let touched = false;
            const list = Object.values(bucket ?? {}).map(entry => {
                const id = idOf(after, entry);
                if (!targetIds.has(id)) return entry;
                const def = after.definition(id);
                if (!def) return entry;
                const projected = materializer.materialize(entry, id, def), row = record(entry);
                if (!projected.charges) return entry;
                const charges = { ...record(row.charges), ...projected.charges };
                if (JSON.stringify(charges) === JSON.stringify(row.charges)) return entry;
                touched = true; return { ...row, charges };
            });
            if (touched) putIntegerCompilePath(work, ['computed', 'creature', 'abilities', key], list);
        }
        host = integerCompileResult(work) as Row;
    }
    if (families.has('spells')) {
        for (const key of ['at_will_spell', 'innate_spell_per_long_rest', 'spell_known', 'curse_known']) {
            if (!changed.has(key)) continue;
            const grants = (list: ItemFieldRow[], context: Context) => list.flatMap(def => entries(context, def, key).map(raw => ({
                id: upper(readLayerListEntryUuid(raw)), source: idOf(context, def),
            })));
            host = savedGrantSpellBucketDelta(host, key, grants(priorSources, before), grants(nextSources, after), after, afterBase, metadata);
        }
    }
    if (changed.has('extra_attack')) {
        const attacks = compileCreatureAttacks(host, { ...after.deps, inventoryAbilityTypes: metadata.rollup.inventoryAbilityTypes,
            queryItemType: id => after.readers.itemField(after.definition(id), 'item_type') ?? '' });
        if (!attacks.ok) throw Error(`saved-grant-delta:${attacks.reason}`);
        const work = integerCompileWorkingRow(host);
        for (const key of ['attacks', 'combat'] as const) {
            if (attacks[key] === undefined) removeIntegerCompilePath(work, ['computed', 'creature', key]);
            else putIntegerCompilePath(work, ['computed', 'creature', key], attacks[key]);
        }
        host = integerCompileResult(work) as Row;
    }
    if (integers.length) {
        const collector = (context: Context) => createDynamicIntegerSourceCollector({ ...context.deps,
            metadata: metadata.rollup, inventoryAbilityTypes: metadata.rollup.inventoryAbilityTypes });
        const oldCollector = collector(before), newCollector = collector(after);
        const work = integerCompileWorkingRow(host);
        for (const key of integers) {
            const [oldSources, oldTotal] = oldCollector.dynamic(previous, key, true, true, null, true,
                (entry, def) => eligible(def, entry, previous));
            const [newSources, newTotal] = newCollector.dynamic(host, key, true, true, null, true,
                (entry, def) => eligible(def, entry, host));
            const stored = Object.values(record(creature(previous).modifier_sources)[key] ?? {}).filter(fromBaseRecord);
            if (stored.some(source => readItemField(source, 'kind') === 'inventory_equipped'
                && readItemField(source, 'value_brace') != null && !phpJsonTruthy(readItemField(source, 'suppressed')))) continue;
            if ([...oldSources, ...newSources].some(source => readItemField(source, 'value_brace') != null))
                throw Error(`saved-grant-delta:unported-braced-integer:${key}`);
            writeIntegerComputeSlotValue(work, key, phpJsonInt(before.readers.layerValue(previous, key)) - oldTotal + newTotal);
            const sourceKey = (row: Row) => ['kind', 'href_id', 'value'].map(key => readItemField(row, key) ?? '').join('|');
            const removed = oldSources.map(sourceKey), kept = stored.filter(row => {
                const at = removed.indexOf(sourceKey(row));
                if (at < 0) return true;
                removed.splice(at, 1); return false;
            });
            const merged = [...kept, ...newSources];
            if (merged.length) putIntegerCompilePath(work, ['computed', 'creature', 'modifier_sources', key], merged);
            else removeIntegerCompilePath(work, ['computed', 'creature', 'modifier_sources', key]);
            if (key === 'max_summons') putIntegerCompilePath(work, ['computed', 'creature', 'meta', key],
                phpJsonInt(before.readers.layerValue(previous, key)) - oldTotal + newTotal || 3);
        }
        host = integerCompileResult(work) as Row;
    }
    if (essences.length) {
        const compile = createCreatureEssenceRollupWithSourceCleanup({ ...after.deps, ...metadata.rollup, metadata: metadata.rollup,
            queryItemType: id => after.readers.itemField(after.definition(id), 'item_type') ?? '',
            source: kind => { throw Error(`saved-grant-delta:unported-essence-source:${kind}`); } });
        for (const essence of essences) {
            // Final normalized rows retain the preliminary base in this native
            // essence cell; the raw prefix's temporary scalar has been retired.
            const seed = integerCompileWorkingRow(host), key = essence.variable_name;
            const layers = record(host.item_layer_types), authored = Object.prototype.hasOwnProperty.call(layers, key);
            const prior = record(creature(previous).essences)[key];
            if (!authored) putIntegerCompilePath(seed, ['item_layer_types', key],
                [before.readers.itemField(record(prior), 'base') ?? (key === 'fortune' ? 2 : 0)]);
            host = compile(integerCompileResult(seed), key, essence.item_id, key === 'fortune' ? 2 : 0) as Row;
            if (!authored) {
                const clean = integerCompileWorkingRow(host);
                removeIntegerCompilePath(clean, ['item_layer_types', key]);
                host = integerCompileResult(clean) as Row;
            }
        }
    }
    if (changed.has('natural_weapon')) {
        const grants = (list: ItemFieldRow[], context: Context) => list.flatMap(def => entries(context, def, 'natural_weapon'));
        const removed = new Set(grants(priorSources, before).map(readLayerListEntryUuid).map(upper));
        const added = grants(nextSources, after), abilities = record(creature(host).abilities);
        const retained = Object.values(abilities.natural_weapon ?? {}).filter(row => !removed.has(upper(readLayerListEntryUuid(row))));
        const natural = [...retained];
        for (const row of added) if (!natural.some(old => upper(readLayerListEntryUuid(old)) === upper(readLayerListEntryUuid(row)))) natural.push(row);
        const work = integerCompileWorkingRow(host);
        putIntegerCompilePath(work, ['computed', 'creature', 'abilities', 'natural_weapon'], natural);
        host = integerCompileResult(work) as Row;
        const recount = createCreatureEquippedCountsCompiler({ ...after.deps, ...metadata.equippedCounts });
        host = recount(host) as Row;
        const counted = integerCompileWorkingRow(host), equipped: Row = { ...record(creature(host).equipped) };
        for (const key of Object.keys(host).filter(key => /^(count_|equipped_)/.test(key))) {
            const value = after.readers.layerValue(host, key), field = key.startsWith('count_') ? key : key.slice(9);
            if (value != null && value !== '' && value !== 0 && value !== '0') equipped[field] = value;
            else delete equipped[field];
            delete counted[key];
        }
        putIntegerCompilePath(counted, ['computed', 'creature', 'equipped'], equipped);
        const skills = createSkillRequirementChecker({ ...after.deps, skillEntries: metadata.skills });
        for (const key of ['attack_ability', 'opportunity_attack_ability']) {
            const retained = Object.values(record(creature(host).skill_ability)[key] ?? {})
                .filter(row => readItemField(record(row), 'built_from') !== 'natural_weapon');
            for (const weapon of natural) {
                const def = after.definition(readLayerListEntryUuid(weapon));
                if (!def) throw Error(`saved-grant-delta:missing-natural-weapon:${readLayerListEntryUuid(weapon)}`);
                for (const row of entries(after, def, key)) {
                    const id = readLayerListEntryUuid(row), ability = after.definition(id);
                    if (!ability) throw Error(`saved-grant-delta:missing-natural-attack:${id}`);
                    if (skills(ability, host) && !retained.some(row => idOf(after, row) === upper(id)))
                        retained.push({ ...record(ability), built_from: 'natural_weapon' });
                }
            }
            putIntegerCompilePath(counted, ['computed', 'creature', 'skill_ability', key], retained);
        }
        host = integerCompileResult(counted) as Row;
    }
    return { host, families: [...families, ...integers, ...essences.map(row => row.variable_name), ...(changed.has('natural_weapon') ? ['natural_weapon'] : [])] };
}
